<template>
    <div class="modal-container">
        <form @submit.prevent="handleSubmit" class="modal-form">
            <div class="modal-header">
                <div class="icon-container">
                    <PhUser :size="24" class="text-gray-500" />
                </div>
                <div class="header-text">
                    <h2>Edit Employee Name</h2>
                    <p>Update the employee's name information</p>
                </div>
            </div>

            <div class="modal-body">
                <div class="form-row">
                    <label>First Name</label>
                    <PrimeInputText
                        v-model="formData.first_name"
                        placeholder="First name"
                        class="w-full"
                    />
                </div>

                <div class="form-row">
                    <label>Middle Name</label>
                    <PrimeInputText
                        v-model="formData.middle_name"
                        placeholder="Middle name (optional)"
                        class="w-full"
                    />
                </div>

                <div class="form-row">
                    <label>Last Name</label>
                    <PrimeInputText
                        v-model="formData.last_name"
                        placeholder="Last name"
                        class="w-full"
                    />
                </div>
            </div>

            <div class="modal-footer">
                <PrimeButton label="Cancel" @click="closeDialog" class="p-btn-root_secondary" :disabled="isLoading" />
                <PrimeButton label="Save" type="submit" :loading="isLoading" />
            </div>
        </form>
    </div>
</template>

<script setup lang="ts">
import { PhUser } from '@phosphor-icons/vue';
import { businessService } from '~/services/business-service';
import { ModalNames } from '~/types/modals';
import { memberFacingMessage } from '~/utils/errors';

const dialogRef = getDialogRef(ModalNames.EDIT_EMPLOYEE_NAME);
const { showSuccess, showError } = useGlobalToast();
const isLoading = ref(false);

const formData = reactive({
    first_name: '',
    middle_name: '',
    last_name: '',
});

const companyId = computed(() => dialogRef.value?.data?.companyId);
const employeeId = computed(() => dialogRef.value?.data?.employeeId);
const employee = computed(() => dialogRef.value?.data?.employee);

onMounted(() => {
    if (employee.value) {
        formData.first_name = employee.value.first_name || '';
        formData.middle_name = employee.value.middle_name || '';
        formData.last_name = employee.value.last_name || '';
    }
});

const closeDialog = () => {
    dialogRef.value.close();
};

const handleSubmit = async () => {
    if (!formData.first_name || !formData.last_name) {
        showError('Error', 'First and last name are required');
        return;
    }

    isLoading.value = true;
    try {
        await businessService.updateEmployee(companyId.value, employeeId.value, {
            first_name: formData.first_name,
            middle_name: formData.middle_name || null,
            last_name: formData.last_name,
        });
        showSuccess('Success', 'Name updated successfully');
        dialogRef.value.close({ updated: true });
    } catch (error) {
        showError('Error', memberFacingMessage(error, 'Failed to update name'));
    } finally {
        isLoading.value = false;
    }
};
</script>

<style lang="scss" scoped>
@use '../../../assets/styles/business-form-modal';
</style>
